// Recursively compare two values, treating null / undefined / '' as equivalent.
// This handles fields the server omits entirely being set to '' by v-model inputs.
function deepEqual(a, b) {
  const emptyish = v => v === null || v === undefined || v === ''
  if (emptyish(a) && emptyish(b)) return true
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => deepEqual(v, b[i]))
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false
    }
    return true
  }
  return false
}

export default {
  data() {
    return {
      modelSnapshot: null,
      saving: false,
    }
  },
  computed: {
    isDirty() {
      if (this.modelSnapshot === null) return false
      // Fast path: identical serialisation
      if (JSON.stringify(this.model) === this.modelSnapshot) return false
      // Slow path: normalised comparison for null/undefined/'' equivalence
      return !deepEqual(this.model, JSON.parse(this.modelSnapshot))
    }
  },
  methods: {
    _resetDirtyTracking() {
      this.modelSnapshot = JSON.stringify(this.model)
      // Clear any stale validation errors and pending timers from the
      // numeric/filter mixins so a discard doesn't leave red fields behind.
      if ('numericErrors' in this) this.numericErrors = {}
      if ('filterErrors' in this) this.filterErrors = {}
      if (this._numericTimers) { Object.values(this._numericTimers).forEach(clearTimeout); this._numericTimers = {} }
      if (this._filterTimers) { Object.values(this._filterTimers).forEach(clearTimeout); this._filterTimers = {} }
      if (this._numericPendingArgs) this._numericPendingArgs = {}
      if (this._filterPendingArgs) this._filterPendingArgs = {}
    },
    _firstDirtyField() {
      if (this.modelSnapshot === null) return null
      const snapshot = JSON.parse(this.modelSnapshot)
      const allKeys = new Set([...Object.keys(this.model), ...Object.keys(snapshot)])
      for (const key of allKeys) {
        if (!deepEqual(this.model[key], snapshot[key])) return key
      }
      return null
    },
    scrollToFirstDirtyField() {
      const field = this._firstDirtyField()
      if (!field) return

      // Resolve which accordion section (toggle key) this field belongs to
      let toggleKey = null

      // 1. JSON editor fields explicitly mapped in each component
      if (this.jsonFieldToggleMap && this.jsonFieldToggleMap[field]) {
        toggleKey = this.jsonFieldToggleMap[field]
      }

      // 2. Regular fields mapped via prefix convention in each component
      if (!toggleKey && this.fieldPrefixSectionMap) {
        for (const [prefix, key] of this.fieldPrefixSectionMap) {
          if (field.startsWith(prefix)) {
            toggleKey = key
            break
          }
        }
      }

      // Track whether section was already open BEFORE we open it
      const sectionWasClosed = toggleKey && this.toggle && !this.toggle[toggleKey]

      // Open the section if we found one
      if (toggleKey && this.toggle !== undefined) {
        this.$set(this.toggle, toggleKey, true)
      }

      // If we just opened a collapsed section wait for its CSS transition (~350ms)
      // before querying layout — otherwise the row is still at height 0
      const delay = sectionWasClosed ? 420 : 0

      const doHighlight = () => {
        // Find the exact field row, fall back to section card, then component root
        const row = field ? this.$el.querySelector(`[data-field="${field}"]`) : null

        let scrollTarget = this.$el
        if (row) {
          scrollTarget = row
        } else if (toggleKey) {
          const cards = this.$el.querySelectorAll('.alert-section-card')
          for (const card of cards) {
            if (card.getAttribute('data-section') === toggleKey) {
              scrollTarget = card
              break
            }
          }
        }

        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Find the input/select/textarea inside the row to highlight, falling
        // back to the row's cells if no input is present (e.g. toggle rows)
        let highlightEls
        if (scrollTarget.tagName === 'TR') {
          const input = scrollTarget.querySelector('input, select, textarea, .custom-select')
          highlightEls = input ? [input] : Array.from(scrollTarget.querySelectorAll('td, th'))
        } else {
          highlightEls = [scrollTarget]
        }

        highlightEls.forEach(el => {
          el.style.transition = 'none'
          el.style.backgroundColor = '#fff3cd'
          el.style.outline = '2px solid #ffc107'
        })
        setTimeout(() => {
          highlightEls.forEach(el => {
            el.style.transition = 'background-color 1.5s ease-out, outline-color 1.5s ease-out'
            el.style.backgroundColor = ''
            el.style.outlineColor = 'transparent'
          })
          setTimeout(() => {
            highlightEls.forEach(el => {
              el.style.transition = ''
              el.style.outline = ''
            })
          }, 1600)
        }, 800)
      }

      this.$nextTick(() => setTimeout(doHighlight, delay))
    },
  }
}
